<template>
  <li class="account-menu-item" :class="{ isMenuExpanded }">
    <ButtonMenu
      :offset="[0, -10]"
      placement="right-start"
      :has-content-padding="false"
    >
      <template #btn="{ isShown }">
        <div
          class="account-trigger"
          :class="{ selected: isShown || isSettingsRoute }"
        >
          <div v-if="personish" class="avatar-wrapper">
            <Avatar :personish="personish" size="small" badge-side="hide" />
            <span
              v-if="!isPremium"
              class="premium-dot"
              aria-label="Upgrade available"
            />
          </div>
          <span v-if="isMenuExpanded" class="name">{{
            displayName || email
          }}</span>
        </div>
      </template>
      <template #content="{ props: hide }">
        <div class="account-header">
          <div v-if="personish" class="avatar-wrapper">
            <Avatar :personish="personish" badge-side="hide" />
          </div>
          <div class="account-info">
            <span v-if="displayName" class="account-name">{{
              displayName
            }}</span>
            <span class="account-email">{{ email }}</span>
          </div>
        </div>
        <div class="account-divider" />
        <div class="account-actions">
          <BaseButton
            v-if="inElectron"
            kind="auxiliary"
            text="Open dashboard"
            is-expanded
            @click="
              () => {
                hide();
                openDashboard();
              }
            "
          >
            <template #icon>
              <DashboardIcon />
            </template>
            <template v-if="dashboardHotkeyLabel" #right>
              <span class="hotkey-hint">{{ dashboardHotkeyLabel }}</span>
            </template>
          </BaseButton>
          <BaseButton
            kind="auxiliary"
            text="Settings"
            is-expanded
            @click="
              () => {
                hide();
                goToSettings();
              }
            "
          >
            <template #icon>
              <SettingsIcon />
            </template>
          </BaseButton>
          <ButtonMenu
            placement="right-start"
            :offset="[0, -4]"
            class="help-submenu"
          >
            <template #btn>
              <BaseButton kind="auxiliary" text="Help" is-expanded>
                <template #icon>
                  <HelpIcon />
                </template>
                <template #right>
                  <span
                    v-if="!isPremium"
                    class="premium-dot premium-dot-inline"
                    aria-label="Upgrade available"
                  />
                  <ChevronRightIcon class="chevron" />
                </template>
              </BaseButton>
            </template>
            <template #content>
              <BaseButton
                v-if="!isPremium"
                kind="auxiliary"
                text="Upgrade"
                tint="red"
                is-expanded
                @click="
                  () => {
                    hide();
                    goToPlan();
                  }
                "
              >
                <template #icon>
                  <PremiumIcon />
                </template>
              </BaseButton>
              <BaseButton
                kind="auxiliary"
                text="What's new?"
                is-expanded
                @click="
                  () => {
                    hide();
                    openUrl('https://feedback.routine.co/changelog');
                  }
                "
              >
                <template #icon>
                  <NewsIcon />
                </template>
              </BaseButton>
              <BaseButton
                kind="auxiliary"
                text="Chat"
                is-expanded
                @click="
                  () => {
                    hide();
                    openUrl('https://routine.co/chat');
                  }
                "
              >
                <template #icon>
                  <ChatIcon />
                </template>
              </BaseButton>
              <BaseButton
                kind="auxiliary"
                text="Help center"
                is-expanded
                @click="
                  () => {
                    hide();
                    openUrl('https://help.routine.co');
                  }
                "
              >
                <template #icon>
                  <SupportIcon />
                </template>
              </BaseButton>
              <BaseButton
                kind="auxiliary"
                text="Report bug"
                is-expanded
                @click="
                  () => {
                    hide();
                    triggerFeedback();
                  }
                "
              >
                <template #icon>
                  <BugIcon />
                </template>
              </BaseButton>
              <BaseButton
                kind="auxiliary"
                text="Suggest feature"
                is-expanded
                @click="
                  () => {
                    hide();
                    triggerFeedback();
                  }
                "
              >
                <template #icon>
                  <IdeaIcon />
                </template>
              </BaseButton>
              <BaseButton
                kind="auxiliary"
                text="Roadmap"
                is-expanded
                @click="
                  () => {
                    hide();
                    openUrl('https://feedback.routine.co/roadmap');
                  }
                "
              >
                <template #icon>
                  <RoadmapIcon />
                </template>
              </BaseButton>
              <BaseButton
                kind="auxiliary"
                text="Keyboard shortcuts"
                is-expanded
                @click="
                  () => {
                    hide();
                    openKbShortcuts();
                  }
                "
              >
                <template #icon>
                  <ShortcutIcon />
                </template>
              </BaseButton>
              <BaseButton
                kind="auxiliary"
                text="Download apps"
                is-expanded
                @click="
                  () => {
                    hide();
                    openUrl('https://routine.co/download');
                  }
                "
              >
                <template #icon>
                  <AppsIcon />
                </template>
              </BaseButton>
              <BaseButton
                kind="auxiliary"
                text="Invite friends"
                is-expanded
                @click="
                  () => {
                    hide();
                    navigateTo(RouteNames.SettingsInvite, router);
                  }
                "
              >
                <template #icon>
                  <InviteIcon />
                </template>
              </BaseButton>
              <BaseButton
                v-if="inElectron"
                kind="auxiliary"
                text="Developer tools"
                is-expanded
                @click="
                  () => {
                    hide();
                    openDevtools();
                  }
                "
              >
                <template #icon>
                  <CodeIcon />
                </template>
              </BaseButton>
            </template>
          </ButtonMenu>
          <div class="account-divider" />
          <BaseButton
            kind="auxiliary"
            text="Logout"
            tint="red"
            is-expanded
            @click="logout"
          />
          <BaseButton
            v-if="isAdmin"
            kind="auxiliary"
            text="Impersonate"
            is-expanded
            @click="
              () => {
                hide();
                openImpersonateModal();
              }
            "
          >
            <template #icon>
              <AdminIcon />
            </template>
          </BaseButton>
        </div>
      </template>
    </ButtonMenu>
  </li>
</template>

<script lang="ts">
import { computed, defineComponent, ref } from "vue";
import { useRoute } from "vue-router";

import BaseButton from "@/components/base/BaseButton.vue";
import Avatar from "@/components/elements/Avatar.vue";
import type { Personish } from "@/components/elements/Avatar.vue";
import ButtonMenu from "@/components/elements/ButtonMenu.vue";
import AdminIcon from "@/components/icons/AdminIcon.vue";
import AppsIcon from "@/components/icons/AppsIcon.vue";
import BugIcon from "@/components/icons/BugIcon.vue";
import ChatIcon from "@/components/icons/ChatIcon.vue";
import ChevronRightIcon from "@/components/icons/ChevronRightIcon.vue";
import CodeIcon from "@/components/icons/CodeIcon.vue";
import DashboardIcon from "@/components/icons/DashboardIcon.vue";
import IdeaIcon from "@/components/icons/IdeaIcon.vue";
import InviteIcon from "@/components/icons/InviteIcon.vue";
import NewsIcon from "@/components/icons/NewsIcon.vue";
import PremiumIcon from "@/components/icons/PremiumIcon.vue";
import RoadmapIcon from "@/components/icons/RoadmapIcon.vue";
import ShortcutIcon from "@/components/icons/ShortcutIcon.vue";
import SupportIcon from "@/components/icons/SupportIcon.vue";
import HelpIcon from "@/components/icons/tabs/HelpIcon.vue";
import SettingsIcon from "@/components/icons/tabs/SettingsIcon.vue";
import * as inject from "@/components/inject";
import { goToKBShortcuts } from "@/components/menu/controller";
import { openImpersonateModal } from "@/components/modals/impersonate";
import { isAdminIdentity } from "@/components/settings/identity";
import { UseFeedback } from "@/hooks/support/feedback";
import { RouteNames } from "@/routes/types";
import { devtools } from "@/utils/devtools";
import { hotkeys } from "@/utils/hotkeys";
import { in_electron } from "@/utils/in";
import { keymap } from "@/utils/keymap";
import { navigateTo, navigateToPlan } from "@/utils/navigate";
import { openExternalUrl } from "@/utils/open-external-url";

export default defineComponent({
  components: {
    AdminIcon,
    AppsIcon,
    Avatar,
    BaseButton,
    BugIcon,
    ButtonMenu,
    ChevronRightIcon,
    CodeIcon,
    ChatIcon,
    DashboardIcon,
    HelpIcon,
    IdeaIcon,
    InviteIcon,
    NewsIcon,
    PremiumIcon,
    RoadmapIcon,
    SettingsIcon,
    ShortcutIcon,
    SupportIcon,
  },
  async setup() {
    const billing = inject.billing();
    const controller = inject.controller();
    const currentRoute = useRoute();
    const isMenuExpanded = inject.menu().isExpanded;
    const logout = inject.logout();
    const profile = inject.profile();
    const router = inject.router();

    const isPremium = computed(() => !!billing.value?.isPremium?.value);
    const email = computed(() => profile.value.user.email);
    const isAdmin = computed(() => isAdminIdentity(email.value));
    const displayName = computed(() => {
      const { givenName, familyName } = profile.value.user;
      const full = [givenName, familyName].filter(Boolean).join(" ");
      return full || "";
    });

    const personish = computed<Personish | null>(() => {
      const user = profile.value.user;
      if (!user.email) return null;
      return {
        kind: "attendee" as const,
        email: user.email,
        name: displayName.value || user.email,
      };
    });

    const isSettingsRoute = computed(() => {
      const name = currentRoute.name?.toString();
      return name === RouteNames.Settings || name?.startsWith("Settings");
    });

    const goToSettings = () => {
      navigateTo(RouteNames.Settings, router);
    };

    const { prepare: triggerFeedback } = await UseFeedback.getInstance();
    const openUrl = (url: string) => openExternalUrl(url, controller);
    const openKbShortcuts = () => goToKBShortcuts(controller);
    const goToPlan = () => navigateToPlan(router, profile.value.user);
    const openDashboard = () => {
      const { ipcRenderer: ipc } = require("electron");
      ipc.send("open-dashboard");
    };
    const dashboardHotkeyLabel = ref("");
    if (in_electron) {
      hotkeys.ipc
        .get(hotkeys.STORE_IDENTIFIERS.DASHBOARD)
        .then((hk) => {
          if (hk) {
            dashboardHotkeyLabel.value =
              hotkeys.format.stringify(
                hk,
                keymap.formatSymbol,
                keymap.separatorSpace
              ) || "";
          }
        })
        .catch(() => {});
    }
    const openDevtools = () => {
      devtools.open();
    };

    return {
      dashboardHotkeyLabel,
      displayName,
      email,
      goToSettings,
      goToPlan,
      inElectron: in_electron,
      isAdmin,
      isMenuExpanded,
      isPremium,
      isSettingsRoute,
      logout,
      navigateTo,
      openDashboard,
      openDevtools,
      openImpersonateModal,
      openKbShortcuts,
      openUrl,
      personish,
      RouteNames,
      router,
      triggerFeedback,
    };
  },
});
</script>

<style scoped lang="scss">
.account-menu-item {
  width: 100%;

  .button-menu {
    width: 100%;
  }

  .account-trigger {
    display: flex;
    align-items: center;
    cursor: default;
    border-radius: $border-radius-s;

    .name {
      margin-inline-start: 10px;
      @extend %font-body-regular;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      @include themed() {
        color: t($grey-60);
      }
    }
  }

  &:not(.isMenuExpanded) {
    .account-trigger {
      justify-content: center;
      padding: 6px;

      &:hover,
      &.selected {
        :deep(.avatar .photo img),
        :deep(.avatar .fallback span) {
          @include themed() {
            box-shadow: t($icon);
          }
        }
      }

      &:active {
        :deep(.avatar .photo img),
        :deep(.avatar .fallback span) {
          @include themed() {
            box-shadow: t($icon-active);
          }
        }
      }
    }
  }

  &.isMenuExpanded {
    .account-trigger {
      width: calc(100% - 24px);
      padding: 7px 12px;
      border-radius: $border-radius-m;
      @include transition(all 100ms ease-out);

      &:hover,
      &.selected {
        @include themed() {
          background: t($bg-menu-hover);
        }
      }

      &:active {
        @include themed() {
          background: t($grey-15);
        }
      }

      &.selected .name {
        font-weight: 500;
        @include themed() {
          color: t($primary);
        }
      }
    }
  }
}

.avatar-wrapper {
  position: relative;
  display: flex;
}

.premium-dot {
  position: absolute;
  height: 6px;
  width: 6px;
  top: -1px;
  inset-inline-end: -1px;
  border-radius: 50px;
  @include themed() {
    background-color: t($secondary-50);
  }
}

.premium-dot-inline {
  position: static;
  flex-shrink: 0;
}

.account-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding-block: 12px;
  padding-inline-end: 24px;
  padding-inline-start: 12px;
  max-width: 220px;
}

.account-info {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.account-name {
  @extend %font-body-medium;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  @include themed() {
    color: t($text);
  }
}

.account-email {
  @extend %font-small-regular;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  @include themed() {
    color: t($grey-40);
  }
}

.account-divider {
  height: 1px;
  margin: 4px 8px;
  @include themed() {
    background-color: t($border);
  }
}

.account-actions {
  padding: $padding-tooltip;

  button.base-button.auxiliary :deep(.left) {
    column-gap: 10px;
  }

  .hotkey-hint {
    @extend %font-small-regular;
    letter-spacing: 0.5px;
    @include themed() {
      color: t($grey-30);
    }
  }

  .help-submenu {
    width: 100%;

    :deep(.button-wrapper) {
      width: 100%;
    }

    .chevron {
      width: 14px;
      height: 14px;
      @include themed() {
        color: t($grey-30);
      }
      :deep(.stroke) {
        @include themed() {
          stroke: t($grey-30);
        }
      }
    }
  }
}

:deep(.button-menu .button-wrapper) {
  justify-content: center;
}

button.base-button.none.auxiliary.isExpanded:not(.disabled):not(.loading):not(
    :active
  ) {
  &:hover {
    @include themed() {
      background-color: t($bg-menu-hover);
    }
  }
}
</style>
